import { defineComponent, ref, Ref, computed, nextTick, onMounted, onUnmounted } from 'vue';
import { IPreferenceManager, PreferenceType, SearchRadiusPreference, PreferenceQueryModel } from '@truckdown/locations';
import context from '@truckdown/components-systems';
import { ILookupService, ServiceModel } from '@truckdown/systems';

interface LocationPreferenceModel {
    id: string;
    locationId: string;
    type: PreferenceType;
    priority?: number;
    radius?: SearchRadiusPreference;
    services: string[];
    accountName: string;
    userName: string;
    modifiedDate: Date | string;
    accountUrl: string;
    userUrl: string;
}

interface IProps {
    items: LocationPreferenceModel[];
    total: number;
    query: PreferenceQueryModel;
    locationId: string;
}

interface IData {
    isLoading: Ref<boolean>,
    pageCount: Ref<number>,
    resetList: Ref<boolean>,
    preferncesCount: Ref<number>,
    listingPreferences: Ref<LocationPreferenceModel[]>,
    preferencesQuery: Ref<PreferenceQueryModel>,
    allServices: Ref<ServiceModel[]>
}

const getData = function (props: IProps): IData {
    return {
        isLoading: ref<boolean>(false),
        pageCount: ref<number>(10),
        resetList: ref<boolean>(false),
        preferncesCount: ref<number>(props.total),
        listingPreferences: ref<LocationPreferenceModel[]>([]),
        preferencesQuery: ref<PreferenceQueryModel>(props.query),
        allServices: ref<ServiceModel[]>([])
    };
}

interface IPageKey {
    lastId?: string
}

const getComputed = function (data: IData) {
    return {
        lastPagerObject: computed(() => {
            if (data.listingPreferences.value && data.listingPreferences.value.length > 0) {
                let last = (data.listingPreferences.value)[data.listingPreferences.value.length - 1];
                return {
                    lastId: last.id
                }
            }
        })
    };
};


const triggerResetList = async function (data: IData, ev?: Event) {
    data.resetList.value = true;
    nextTick(() => {
        data.resetList.value = false;
    });
}

const loadPreferences = async function (id: string, data: IData, page?: IPageKey) {
    if (!data.isLoading.value && data.preferencesQuery.value) {
        data.isLoading.value = true;
        try {
            if (page) {
                data.preferencesQuery.value.lastId = page.lastId;
            } else {
                data.preferencesQuery.value.lastId = undefined;
            }

            const service = await context.getService<IPreferenceManager>('IPreferenceManager');
            const result = await service.getLocationPreferences(id, data.preferencesQuery.value);

            if (result?.results) {
                data.listingPreferences.value = result.results;
                data.preferncesCount.value = result.count ?? 0;
                if (!page) {
                    await triggerResetList(data);
                }
            } else {
                console.error("Error querying listing preferences.");
            }

        } catch (err) {
            console.error("Error querying listing preferences.");
        } finally {
            data.isLoading.value = false;
        }
    }
};


const setServices = async function (data: IData) {
    try {
        const service = await context.getService<ILookupService>('ILookupService');
        const result = await service.getServices();
        if (result !== null) {
            data.allServices.value = result;
        }
    } catch (reason) {
        console.error('Error getting services', reason);
    }
};

const getMethods = function (props: IProps, data: IData, ctx: any) {
    return {
        updateList: async function (updateArgs?: IPageKey) {
            await loadPreferences(props.locationId, data, updateArgs);
        },
        updateTab: async function (type: PreferenceType) {
            if (data.preferencesQuery.value.type !== type) {
                data.preferencesQuery.value.type = type;
                let hash = type === PreferenceType.Avoid ? "avoided" : "favorites";
                window.location.hash = hash;
                await loadPreferences(props.locationId, data);
            }
        }
    }
}

export default defineComponent({
    name: "location-preferences",
    emits: [],
    props: {
        items: {
            type: Array as () => LocationPreferenceModel[],
            required: true
        },
        total: {
            type: Number,
            required: true
        },
        query: {
            type: Object as () => PreferenceQueryModel,
            required: true
        },
        locationId: {
            type: String,
            required: true
        }
    },
    setup: function (props, ctx) {
        let data = getData(props);
        let methods = getMethods(props, data, ctx);
        let computed = getComputed(data);

        function mapServices(items: LocationPreferenceModel[], lookup: ServiceModel[]) {
            const lookupMap = new Map(
                lookup.map(s => [s.code, s.name])
            );

            return items.map(item => ({
                ...item,
                services: item.services.map(code => lookupMap.get(code) || code)
            }));
        }

        onMounted( async () => {
            await setServices(data);
            await processHash();
            window.addEventListener('hashchange', processHash);
            data.listingPreferences.value = mapServices(props.items, data.allServices.value);
        });

        const processHash = async function () {
            let hash = window.location.hash;
            if (hash && hash.length > 0) {
                scrollToTop();
                switch (hash) {
                    case "#avoided":
                        methods.updateTab(PreferenceType.Avoid);
                        break;
                    case "#favorites":
                        methods.updateTab(PreferenceType.Preferred);
                        break;
                }
            } else {
                window.location.hash = "#favorites";
            }
        }

        const scrollToTop = () => {
            window.scrollTo({ top: 0, behavior: "smooth" });
        };

        onUnmounted(() => {
            window.removeEventListener('hashchange', processHash);
        });


        return {
            ...data,
            ...methods,
            ...computed,
            'PreferenceType': PreferenceType
        };
    }
});